import { styled } from "goober";
import { nanoid } from "nanoid";
import { useState } from "react";
import { createPortal } from "react-dom";

const BasePopover = styled("div")`
  position: fixed;
  border-radius: 12px;
`;

type Props = {
  inline?: boolean;
  isOpen: boolean;
  trigger: (props: { style: React.CSSProperties }) => React.ReactNode;
  children: React.ReactNode;
  popoverRef?: React.Ref<HTMLDivElement>;
  className?: string;
  id?: string;
  role?: React.AriaRole;
  "aria-labelledby"?: string;
  target?: string;
  popoverAnchorName?: string;
  style?: React.CSSProperties;
  direction?: "down" | "up" | "left" | "right";
};

export function AnchoredPopover({
  inline = false,
  isOpen,
  trigger,
  children,
  popoverRef,
  className,
  id: elementId,
  role,
  "aria-labelledby": ariaLabelledBy,
  target,
  popoverAnchorName,
  style,
  direction,
}: Props) {
  const [id] = useState(() => `--popover-${nanoid()}`);
  const [container, setContainer] = useState<Element | null>(null);
  const Wrapper = inline ? "span" : "div";
  const resolveContainer = (el: HTMLElement | null) => {
    if (!el) return;
    setContainer(el.closest("[data-popover-host], dialog") ?? document.body);
  };

  return (
    <Wrapper ref={resolveContainer} style={{ display: "contents" }}>
      {trigger({ style: { anchorName: id } as React.CSSProperties })}
      {isOpen &&
        container &&
        createPortal(
          <BasePopover
            ref={popoverRef}
            className={className}
            id={elementId}
            role={role}
            aria-labelledby={ariaLabelledBy}
            data-popover
            data-direction={direction}
            style={
              {
                ...style,
                positionAnchor: target ?? id,
                anchorName: popoverAnchorName,
                "--anchor-top": target ?? id,
                "--anchor-left": target ?? id,
              } as React.CSSProperties
            }
          >
            {children}
          </BasePopover>,
          container
        )}
    </Wrapper>
  );
}
